import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import range from "lodash/range";
import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getLaborVitalsRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface LaborVitalsRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
  isPreviousVersion?: boolean;
}

export default function LaborVitalsRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
  isPreviousVersion,
}: LaborVitalsRowProps) {
  const { bloodPressure, pulse, temperature, notes, hasAnswers, hasNotes } = useMemo(
    () => getLaborVitalsRowValues(observation),
    [observation],
  );

  const previousVersions = hasPreviousVersions(observation);

  // Determine if details row is needed
  const detailsRowNeeded =
    !showNumbersOnly && (hasAnswers || hasNotes || notes || isPreviousVersion);

  // Row span for TimeCell and ActionCell
  const rowSpan = hasAnswers && detailsRowNeeded && !showNumbersOnly ? 2 : 1;

  // Show vitals summary and notes only if there are notes or this is a previous version
  const showDetailsSummary = isPreviousVersion;

  // Value row: only if there are answers and not a previous version
  return (
    <Fragment>
      {hasAnswers && !isPreviousVersion && showPreviousVersions && (
        <Table.Tr bg="white">
          <TimeCell
            time={time}
            rowSpan={rowSpan}
            isViewOnly={isViewOnly}
            observation={observation}
          />
          <Table.Td />
          <Table.Td>{bloodPressure}</Table.Td>
          <Table.Td>{temperature}</Table.Td>
          <Table.Td>{pulse}</Table.Td>
          {range(6).map((i) => (
            <Table.Td key={i} />
          ))}
          {!isViewOnly && <ActionCell rowSpan={rowSpan} observation={observation} />}
        </Table.Tr>
      )}
      {/* Details row: if there are notes/details or isPreviousVersion is true */}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          {showPreviousVersions && (!hasAnswers || isPreviousVersion) && (
            <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
          )}
          <Table.Td p="md" colSpan={10}>
            {showDetailsSummary && (
              <>
                <TextWrapper pt="xs" size="sm">
                  Labor Vitals:
                </TextWrapper>
                <List fz="sm" withPadding>
                  {bloodPressure && (
                    <ListItem>
                      <strong>Blood Pressure:</strong> {bloodPressure}
                    </ListItem>
                  )}
                  {temperature && (
                    <ListItem>
                      <strong>Temperature:</strong> {temperature}
                    </ListItem>
                  )}
                  {pulse && (
                    <ListItem>
                      <strong>Heart Rate:</strong> {pulse}
                    </ListItem>
                  )}
                </List>
              </>
            )}
            {notes && (
              <TextWrapper pt="xs" size="sm">
                Labor Vitals Comments: {notes}
              </TextWrapper>
            )}
            {showPreviousVersions && previousVersions ? (
              <PreviousVersions
                observation={observation}
                showPreviousVersions={showPreviousVersions}
                rowComponent={(props) => <LaborVitalsRow {...props} isPreviousVersion={true} />}
              />
            ) : (
              <LastUpdated observation={observation} showPreviousVersions={false} />
            )}
          </Table.Td>
          {/* Only render ActionCell in details row if value row is not present and not a previous version */}
          {(!hasAnswers || isPreviousVersion) && !isPreviousVersion && !isViewOnly && (
            <ActionCell rowSpan={1} observation={observation} />
          )}
        </Table.Tr>
      )}
    </Fragment>
  );
}
